import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createLiveSession,
  deleteLiveSession,
  endLiveSession,
  getZoomStatus,
  provisionZoomMeeting,
  startLiveSession,
  updateLiveSession,
} from '@/lib/api';
import { groupSessions } from '@/lib/courseRoom';
import { dateTimeLocalToIsoValue, toDateTimeLocalInputValue } from '@/lib/dates';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CalendarClock, ExternalLink, Info, Pencil, Plus, Radio, Square, Trash2, Video } from 'lucide-react';

const EMPTY = {
  title: '', scheduled_time: '', duration_minutes: 60,
  meeting_link: '', description: '', recording_url: '',
};

function SessionRow({ session, isEditor, busy, onStart, onEnd, onEdit, onDelete, onJoin, onZoom }) {
  const isLive = session.status === 'live';
  const finished = session.status === 'completed' || session.status === 'cancelled';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[rgba(6,43,26,0.08)] bg-white p-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-sm font-semibold text-[#062B1A]">{session.title}</p>
          {isLive && (
            <Badge className="border-0 bg-green-100 text-[10px] text-green-700">
              <Radio className="mr-1 size-2.5" />Live now
            </Badge>
          )}
          {finished && <Badge variant="outline" className="text-[10px]">{session.status}</Badge>}
        </div>
        {session.description && <p className="mt-1 text-xs text-[#062B1A]/60">{session.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-4 text-xs text-[#062B1A]/40">
          <span className="flex items-center gap-1">
            <CalendarClock className="size-3" />
            {session.scheduled_time ? new Date(session.scheduled_time).toLocaleString() : 'Not scheduled'}
          </span>
          <span>{session.duration_minutes || 60} min</span>
        </div>
        {!session.can_join && session.join_reason && !finished && (
          <p className="mt-1.5 text-xs text-[#062B1A]/45">{session.join_reason}</p>
        )}
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {isEditor && !finished && (
          isLive ? (
            <Button size="sm" variant="outline" className="border-red-200 text-red-700 hover:bg-red-50" disabled={busy} onClick={onEnd}>
              <Square className="mr-1.5 size-3.5" />End
            </Button>
          ) : (
            <Button size="sm" className="bg-green-700 text-white hover:bg-green-800" disabled={busy} onClick={onStart}>
              <Radio className="mr-1.5 size-3.5" />Go live
            </Button>
          )
        )}
        {session.can_join && (session.meeting_link || session.meeting_provider === 'zoom') && (
          <Button size="sm" className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={onJoin}>
            <Video className="mr-1.5 size-3.5" />{isEditor ? 'Open room' : 'Join'}
          </Button>
        )}
        {isEditor && !session.meeting_link && session.meeting_provider !== 'zoom' && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onZoom}>Create Zoom</Button>
        )}
        {session.recording_url && (
          <Button size="sm" variant="outline" onClick={() => window.open(session.recording_url, '_blank', 'noopener,noreferrer')}>
            <ExternalLink className="mr-1.5 size-3.5" />Replay
          </Button>
        )}
        {isEditor && (
          <>
            <Button size="sm" variant="outline" onClick={onEdit}><Pencil className="size-3.5" /></Button>
            <Button size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50" onClick={onDelete} aria-label={`Delete ${session.title}`}>
              <Trash2 className="size-3.5" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Live classes inside the course room.
 *
 * The instructor schedules and runs the class from here; the student joins from
 * the same list. Admission is decided by the server and arrives on each session
 * as `can_join`, so the button and the outcome of pressing it always agree.
 */
export default function LiveTab({ courseId, sessions, isEditor, roomBasePath, onChanged }) {
  const navigate = useNavigate();
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busyId, setBusyId] = useState('');
  const [saving, setSaving] = useState(false);
  const [zoomStatus, setZoomStatus] = useState(null);
  const [checkedZoom, setCheckedZoom] = useState(false);

  if (isEditor && !checkedZoom) {
    setCheckedZoom(true);
    getZoomStatus().then(({ data }) => setZoomStatus(data)).catch(() => {});
  }

  const groups = groupSessions(sessions);

  const openCreate = () => { setEditor({ mode: 'create' }); setForm(EMPTY); };
  const openEdit = (session) => {
    setEditor({ mode: 'edit', session });
    setForm({
      title: session.title || '',
      scheduled_time: toDateTimeLocalInputValue(session.scheduled_time),
      duration_minutes: session.duration_minutes || 60,
      meeting_link: session.meeting_link || '',
      description: session.description || '',
      recording_url: session.recording_url || '',
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Give the session a title');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, scheduled_time: dateTimeLocalToIsoValue(form.scheduled_time) };
      if (editor.mode === 'create') {
        await createLiveSession({ ...payload, course_id: courseId, status: 'upcoming' });
        toast.success('Session scheduled');
      } else {
        await updateLiveSession(editor.session.id, payload);
        toast.success('Session updated');
      }
      setEditor(null);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save this session');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (session, action) => {
    try {
      setBusyId(session.id);
      if (action === 'start') await startLiveSession(session.id);
      else if (action === 'end') await endLiveSession(session.id);
      else if (action === 'delete') await deleteLiveSession(session.id);
      else if (action === 'zoom') await provisionZoomMeeting(session.id);
      toast.success({
        start: 'Session is live — students can join now',
        end: 'Session ended',
        delete: 'Session deleted',
        zoom: 'Zoom meeting created',
      }[action]);
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update this session');
    } finally {
      setBusyId('');
    }
  };

  const rowProps = (session) => ({
    session,
    isEditor,
    busy: busyId === session.id,
    onStart: () => runAction(session, 'start'),
    onEnd: () => runAction(session, 'end'),
    onZoom: () => runAction(session, 'zoom'),
    onDelete: () => runAction(session, 'delete'),
    onEdit: () => openEdit(session),
    onJoin: () => navigate(`${roomBasePath}/live-sessions/${session.id}`),
  });

  return (
    <div className="space-y-4" data-testid="room-live">
      {isEditor && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" className="bg-[#062B1A] text-white hover:bg-[#041B10]" onClick={openCreate}>
            <Plus className="mr-2 size-4" />Schedule a class
          </Button>
        </div>
      )}

      {isEditor && zoomStatus && !zoomStatus.in_portal_streaming_ready && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <Info className="mt-0.5 size-4 shrink-0 text-amber-700" />
          <div className="text-xs text-amber-900">
            <p className="font-semibold">Streaming inside the portal is not switched on yet.</p>
            <p className="mt-1">
              Paste a Google Meet, Teams or Zoom link into a session and students join it from here —
              attendance is recorded either way. To host the video inside the portal, these need
              values on the backend:
            </p>
            <p className="mt-1.5 font-mono">
              {[...(zoomStatus.missing_api || []), ...(zoomStatus.missing_sdk || [])].join(', ')}
            </p>
          </div>
        </div>
      )}

      {sessions.length === 0 && (
        <p className="py-8 text-center text-sm text-[#062B1A]/45">
          {isEditor ? 'No classes scheduled yet.' : 'No live classes have been scheduled.'}
        </p>
      )}

      {groups.live.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-green-600">Live now</p>
          {groups.live.map((session) => <SessionRow key={session.id} {...rowProps(session)} />)}
        </div>
      )}
      {groups.upcoming.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40">Upcoming</p>
          {groups.upcoming.map((session) => <SessionRow key={session.id} {...rowProps(session)} />)}
        </div>
      )}
      {groups.past.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/40">Past</p>
          {groups.past.map((session) => <SessionRow key={session.id} {...rowProps(session)} />)}
        </div>
      )}

      <Dialog open={!!editor} onOpenChange={(open) => !open && setEditor(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>
              {editor?.mode === 'create' ? 'Schedule' : 'Edit'} live class
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Title *</label>
              <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Scheduled time</label>
                <Input type="datetime-local" value={form.scheduled_time} onChange={(e) => setForm((p) => ({ ...p, scheduled_time: e.target.value }))} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Duration (min)</label>
                <Input type="number" value={form.duration_minutes} onChange={(e) => setForm((p) => ({ ...p, duration_minutes: parseInt(e.target.value, 10) || 0 }))} />
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Meeting link</label>
              <Input value={form.meeting_link} onChange={(e) => setForm((p) => ({ ...p, meeting_link: e.target.value }))} placeholder="https://..." />
              <p className="mt-1 text-xs text-[#062B1A]/40">Zoom, Google Meet or Teams — students join it from inside the portal.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Recording link</label>
              <Input value={form.recording_url} onChange={(e) => setForm((p) => ({ ...p, recording_url: e.target.value }))} placeholder="https://..." />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Description</label>
              <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditor(null)}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" disabled={saving} onClick={handleSave}>
              {editor?.mode === 'create' ? 'Schedule' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
